import { useGetRolesApiV1UsersRolesGetQuery, UserReadDto, useSearchUserApiV1UsersUserIdGetQuery } from "@/store/baseClientApi";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useState } from "react";

export const useRoles = (args?: { canOperateOnUserWithId?: string; canOperateOnTargetUser?: UserReadDto }) => {
  const { canOperateOnUserWithId, canOperateOnTargetUser } = args ?? {};
  const { getIdTokenClaims } = useAuth0();

  // the current users id
  const [userId, setUserId] = useState<string | undefined>();
  const operandUserIsOperatorUser =
    userId !== undefined && (canOperateOnUserWithId === userId || canOperateOnTargetUser?.user_id === userId);

  useEffect(() => {
    const getOrg = async () => {
      const claims = await getIdTokenClaims();

      setUserId(claims?.sub);
    };
    if (userId === undefined) {
      getOrg();
    }
  }, [getIdTokenClaims, userId]);

  // the current users roles
  const {
    userRoles,
    error: rolesError,
    isLoading: rolesIsLoading
  } = useSearchUserApiV1UsersUserIdGetQuery(
    { userId: encodeURI(userId ?? "") },
    {
      selectFromResult: ({ data, error, isLoading }) => ({
        userRoles: data && data.roles,
        error,
        isLoading
      }),
      skip: userId === undefined
    }
  );

  // all roles
  const { data: roles, error: allRolesError, isLoading: allRolesIsLoading } = useGetRolesApiV1UsersRolesGetQuery();

  // the roles that are less privileged than the current users roles
  const lessPrivilegedRoles = useMemo(() => {
    const userPrivilege =
      userRoles?.reduce((acc, role) => Math.min(acc, role.privilege), Number.MAX_SAFE_INTEGER) ?? Number.MAX_SAFE_INTEGER;

    return roles?.roles?.filter((role) => userPrivilege < role.privilege) ?? [];
  }, [userRoles, roles]);

  // the roles of the user specified by canOperateOnUserWithId
  const {
    operandUserRoles,
    error: operandUserError,
    isLoading: operandUserLoading
  } = useSearchUserApiV1UsersUserIdGetQuery(
    {
      userId: encodeURI(canOperateOnUserWithId ?? "")
    },
    {
      selectFromResult: ({ data, error, isLoading }) => ({
        operandUserRoles: data && data.roles,
        error,
        isLoading
      }),
      skip:
        canOperateOnUserWithId === "" ||
        canOperateOnUserWithId === undefined ||
        operandUserIsOperatorUser ||
        canOperateOnTargetUser !== undefined
    }
  );

  let canOperateOnUser = false;
  if (canOperateOnTargetUser !== undefined) {
    canOperateOnUser =
      operandUserIsOperatorUser || lessPrivilegedRoles.find((role) => role.id === canOperateOnTargetUser?.roles?.[0]?.id) !== undefined;
  }
  if (operandUserRoles !== undefined) {
    canOperateOnUser = operandUserIsOperatorUser || lessPrivilegedRoles.find((role) => role.id === operandUserRoles?.[0]?.id) !== undefined;
  }

  return {
    userId,
    canOperateOnUser: canOperateOnUserWithId !== undefined || canOperateOnTargetUser !== undefined ? canOperateOnUser : undefined,
    userRoles: userRoles,
    lessPrivilegedRoles,
    roles: roles?.roles,
    isLoading: allRolesIsLoading || rolesIsLoading || userId === undefined || operandUserLoading,
    error: rolesError || allRolesError || operandUserError
  };
};
